import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Maximize2 } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import ImageLightbox from "@/components/ImageLightbox";

/**
 * Shared "choose an image from the catalog" modal: collection → product → photo,
 * with breadcrumb navigation and search at each step. Self-contained (loads its
 * own data, cached for the component's lifetime). Calls onPick(imagePath).
 */
export default function ProductImagePicker({ open, onClose, onPick }) {
    const t = useT();
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [cat, setCat] = useState(null);
    const [product, setProduct] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(null);
    const loadedRef = useRef(false);

    useEffect(() => { setPreviewIndex(null); }, [product, open]);

    useEffect(() => {
        if (!open) return;
        setSearch("");
        setCat(null);
        setProduct(null);
        if (loadedRef.current) return;
        setLoading(true);
        Promise.all([
            api.get("/categories"),
            api.get("/products", { params: { page_size: 1000 } }),
        ])
            .then(([c, p]) => {
                setCategories(c.data ?? []);
                setProducts((p.data?.items ?? []).filter((x) => x.images?.length));
                loadedRef.current = true;
            })
            .catch(() => toast.error("Failed to load products"))
            .finally(() => setLoading(false));
    }, [open]);

    if (!open) return null;

    const q = search.trim().toLowerCase();
    const inCat = (p) => p.category_ids?.includes(cat) || p.category_id === cat;

    return (
        <div
            className="fixed inset-0 z-[60] bg-melilux-bg/90 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={onClose}
            data-testid="cover-picker"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-4xl bg-melilux-bg border border-white/15 p-8 max-h-[88vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between gap-4 mb-3">
                    <h2 className="font-serif text-2xl">{t("admin.cat.picker.title")}</h2>
                    <button
                        onClick={onClose}
                        className="p-2 text-melilux-cream/60 hover:text-melilux-cream transition-colors"
                        data-testid="cover-picker-close"
                    >
                        <X size={18} strokeWidth={1.5} />
                    </button>
                </div>

                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.2em] mb-5 flex-wrap">
                    <button
                        type="button"
                        onClick={() => { setCat(null); setProduct(null); setSearch(""); }}
                        className={!cat ? "text-melilux-gold" : "text-melilux-cream/50 hover:text-melilux-cream transition-colors"}
                    >
                        {t("admin.cat.picker.step.collection")}
                    </button>
                    {cat && (
                        <>
                            <span className="text-melilux-cream/30">›</span>
                            <button
                                type="button"
                                onClick={() => { setProduct(null); setSearch(""); }}
                                className={!product ? "text-melilux-gold" : "text-melilux-cream/50 hover:text-melilux-cream transition-colors"}
                            >
                                {categories.find((c) => c.id === cat)?.name || "—"}
                            </button>
                        </>
                    )}
                    {product && (
                        <>
                            <span className="text-melilux-cream/30">›</span>
                            <span className="text-melilux-gold">{product.name}</span>
                        </>
                    )}
                </div>

                {loading ? (
                    <p className="text-center py-12 text-melilux-cream/50">{t("admin.cat.picker.loading")}</p>
                ) : (() => {
                    // STEP 3 — images of the chosen product
                    if (product) {
                        return (
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                                {product.images.map((img, i) => (
                                    <div
                                        key={i}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => onPick(img)}
                                        onKeyDown={(e) => { if (e.key === "Enter") onPick(img); }}
                                        className="relative aspect-square overflow-hidden border border-white/10 hover:border-melilux-gold transition-colors group cursor-pointer"
                                        data-testid={`cover-picker-img-${product.id}-${i}`}
                                    >
                                        <img src={resolveImg(img, 320)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setPreviewIndex(i); }}
                                            aria-label="Preview"
                                            data-testid={`cover-picker-zoom-${i}`}
                                            className="absolute top-1.5 right-1.5 p-1.5 bg-black/60 backdrop-blur-sm text-white/85 hover:text-melilux-gold transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100"
                                        >
                                            <Maximize2 size={13} strokeWidth={1.5} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        );
                    }

                    // STEP 2 — products inside the chosen collection
                    if (cat) {
                        const prods = products
                            .filter(inCat)
                            .filter((p) => !q || p.name.toLowerCase().includes(q));
                        return (
                            <>
                                <input
                                    type="text"
                                    placeholder={t("admin.cat.picker.search")}
                                    className="editorial-input max-w-sm mb-6"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    data-testid="cover-picker-search"
                                />
                                {prods.length === 0 ? (
                                    <p className="text-center py-12 text-melilux-cream/50">{t("admin.cat.picker.noProducts")}</p>
                                ) : (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                        {prods.map((p) => (
                                            <button
                                                key={p.id}
                                                type="button"
                                                onClick={() => { setProduct(p); setSearch(""); }}
                                                className="text-left border border-white/10 hover:border-melilux-gold transition-colors group"
                                                data-testid={`cover-picker-product-${p.id}`}
                                            >
                                                <div className="aspect-square overflow-hidden">
                                                    <img src={resolveImg(p.images[0], 320)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />
                                                </div>
                                                <div className="px-2 py-2 text-xs text-melilux-cream/80 truncate">{p.name}</div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        );
                    }

                    // STEP 1 — collections that contain at least one product with images
                    const cats = categories
                        .filter((c) => products.some((p) => p.category_ids?.includes(c.id) || p.category_id === c.id))
                        .filter((c) => !q || c.name.toLowerCase().includes(q));
                    return (
                        <>
                            <input
                                type="text"
                                placeholder={t("admin.cat.picker.searchCollections")}
                                className="editorial-input max-w-sm mb-6"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                data-testid="cover-picker-search-collections"
                            />
                            {cats.length === 0 ? (
                                <p className="text-center py-12 text-melilux-cream/50">{t("admin.cat.picker.empty")}</p>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                    {cats.map((c) => {
                                        const n = products.filter((p) => p.category_ids?.includes(c.id) || p.category_id === c.id).length;
                                        return (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onClick={() => { setCat(c.id); setSearch(""); }}
                                                className="text-left border border-white/10 hover:border-melilux-gold transition-colors group"
                                                data-testid={`cover-picker-collection-${c.id}`}
                                            >
                                                <div className="aspect-square overflow-hidden bg-white/5">
                                                    {c.cover_image && <img src={resolveImg(c.cover_image, 320)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />}
                                                </div>
                                                <div className="px-2 py-2">
                                                    <div className="text-xs text-melilux-cream/85 truncate">{c.name}</div>
                                                    <div className="text-[0.6rem] uppercase tracking-[0.2em] text-melilux-cream/40 mt-0.5">{n}</div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    );
                })()}
            </div>

            {/* Full-size preview with "use this photo" action.
                stopPropagation so lightbox clicks don't bubble to the picker backdrop and close it. */}
            {previewIndex !== null && product && (
                <div onClick={(e) => e.stopPropagation()}>
                    <ImageLightbox
                        images={product.images.map((img) => resolveImg(img))}
                        startIndex={previewIndex}
                        alt={product.name}
                        onClose={() => setPreviewIndex(null)}
                        actionLabel={t("admin.cat.picker.use")}
                        onAction={(i) => { setPreviewIndex(null); onPick(product.images[i]); }}
                    />
                </div>
            )}
        </div>
    );
}
